/** @description 匹配文字 条件编辑form */
import { Row, Col, Button, Select, Input, AutoComplete } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { ConditionItem, NodeSelectItem } from '@/components/index';
import { arrayMoveImmutable } from 'array-move';
import _ from 'lodash';
import React, { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'umi';
import { CustomTable, DragHandle } from './on-table';
import type { ColumnsType } from 'antd/es/table';

const { Option } = Select;

type ConditionFormProps = {
  conditions: ConditionItem[];
  nodeSelectList: NodeSelectItem[];
  currentNodeId: string;
  currentNodeType?: string;
  nodeKey: string | null;
  condition: string;
  setNodeKey: (e: string) => void;
  setCondition: (e: string) => void;
  handleConditionDelete: (e: string) => void;
  addNodes: () => void;
  replaceNodes: (conditions: ConditionItem[]) => void;
};

const ConditionForm: React.FC<ConditionFormProps> = ({
  conditions,
  nodeSelectList,
  currentNodeId,
  currentNodeType,
  nodeKey,
  setNodeKey,
  handleConditionDelete,
  condition,
  setCondition,
  addNodes,
  replaceNodes,
}) => {
  const regexList = useSelector((state: any) => state.open.regexList);
  const conditionEdit = useSelector((state: any) => state.open.conditionEdit);
  const dispatch = useDispatch();

  useEffect(() => {
    if (conditionEdit.isConfirm && currentNodeId === conditionEdit.nodeId) {
      setCondition(conditionEdit.condition);
    } 
  }, [conditionEdit.isConfirm]);

  const regexListFormat = useMemo(() => {
    return regexList.filter((r: { value: string }) => r.value.indexOf(condition) > -1);
  }, [regexList, condition])

  const nodeObjByKey = useMemo(() => {
    if (!nodeSelectList.length) return {};
    return _.keyBy(nodeSelectList, "key");
  }, [nodeSelectList]);

  /*** @description 调整数组元素顺序 */
  const onSortEnd = (key: string, num: number) => {
    const oldIndex = _.findIndex(conditions, function (o) {
      return o.key === key;
    });
    const newIndex = oldIndex + num;
    const newData = arrayMoveImmutable(conditions, oldIndex, newIndex);
    replaceNodes(newData);
  };

  const contentStyle = {
    color: 'rgb(102, 102, 102)',
    background: 'rgb(242, 235, 233)',
    borderRadius: '4px',
    width: '100%',
    padding: '2px 6px',
    wordBreak: 'break-word',
    textAlign: 'start'
  } as React.CSSProperties;
  const rowStyle = {
    padding: '4px 0',
    borderBottom: '1px dashed rgba(0, 0, 0, 0.1)',
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
  } as React.CSSProperties;

  const handleOptionDisabled = (n: NodeSelectItem) => {
    if (n.key === currentNodeId || n.key === 'introduction') return true;
    if (currentNodeType !== 'globalNode' && n.type !== 'globalNode') return false;
    if (currentNodeType === 'globalNode' && n.type === 'globalNode') return false;
    return true;
  }

  const handleConditionEdit = () => {
    dispatch({
      type: 'open/handleConditionEdit',
      payload: { 
        show: true, 
        isConfirm: false, 
        nodeId: currentNodeId,
        condition: '',
        isEdit: false,
      } 
    })
  }

  const handleConditionEditWithNode = (condition: string) => {
    dispatch({
      type: 'open/handleConditionEdit',
      payload: { 
        show: true, 
        isConfirm: false, 
        nodeId: currentNodeId,
        condition,
        isEdit: true,
      } 
    })
  }

  const columns: ColumnsType<ConditionItem> = [
    {
      title: '排序',
      dataIndex: 'sort',
      className: 'drag-visible',
      width: 60,
      render: () => <DragHandle />
    },
    {
      title: '内容',
      dataIndex: 'content',
      render: (_, c) => {
        return <Row key={c.key}>
        <Col span={22}>
          <Row style={{width: '100%', cursor: 'pointer'}} onClick={() => handleConditionEditWithNode(c.condition)}>
            <div style={contentStyle}>
              {c.condition}
            </div>
          </Row>
          <Row style={{width: '100%'}}>
            {nodeObjByKey[c.key]?.label ?? ''}
          </Row>
        </Col>
      </Row>
      }
    },
    {
      title: '删除',
      dataIndex: 'delete',
      width: 60,
      render: (_: any, c: ConditionItem) => <CloseOutlined onClick={() => handleConditionDelete(c.key)} />
    },
  ]

  const BaseTable = CustomTable<ConditionItem>();

  return (
    <>
      <BaseTable 
        data={conditions}
        columns={columns}
        setDataSource={replaceNodes}
      />
      <Row style={rowStyle}>
        <Select
          placeholder="请选择节点(不选则会新建一个节点)"
          allowClear
          style={{ width: '100%' }}
          value={nodeKey}
          onChange={(e) => setNodeKey(e)}
        >
          {nodeSelectList.map((n: NodeSelectItem) => (
            <Option
              key={n.key}
              value={n.key}
              disabled={handleOptionDisabled(n)}
            >
              {n.label}
            </Option>
          ))}
        </Select>
      </Row>
      <Row style={rowStyle}>
        <Col span={18}>
          <Input.Group compact>
            <AutoComplete
              value={condition}
              style={{ width: '100%' }}
              placeholder="请选择或输入正则"
              options={regexListFormat}
              onChange={(e) => setCondition(e)}
            />
          </Input.Group>
        </Col>
        <Col span={6}>
          <Button type="primary" onClick={handleConditionEdit} style={{width: '100%'}}>
            图形化定义
          </Button>
        </Col>
      </Row>
      <Row style={rowStyle}>
        <Button type="primary" onClick={addNodes}>
          新增一条
        </Button>
      </Row>
    </>
  );
};

export default React.memo(ConditionForm);
